import type { LocalizedEntityName, TutorialEntityNames } from "@pimo/tutorial";
import type { ItemName, ProjectName } from "product-test-types";

const PROJECT_ENTITY_NAMES: Record<ProjectName, LocalizedEntityName> = {
  Project: {
    en: { singular: "Project", plural: "Projects" },
    de: {
      singular: "Projekt",
      plural: "Projekte",
      gender: "neuter",
      compoundStem: "Projekt",
    },
  },
  Measure: {
    en: { singular: "Measure", plural: "Measures" },
    de: {
      singular: "Maßnahme",
      plural: "Maßnahmen",
      gender: "feminine",
      compoundStem: "Maßnahmen",
    },
  },
  Finding: {
    en: { singular: "Finding", plural: "Findings" },
    de: {
      singular: "Befund",
      plural: "Befunde",
      gender: "masculine",
      compoundStem: "Befund",
    },
  },
  Company: {
    en: { singular: "Company", plural: "Companies" },
    de: {
      singular: "Unternehmen",
      plural: "Unternehmen",
      gender: "neuter",
      compoundStem: "Unternehmens",
    },
  },
  Subsidiary: {
    en: { singular: "Subsidiary", plural: "Subsidiaries" },
    de: {
      singular: "Tochtergesellschaft",
      plural: "Tochtergesellschaften",
      gender: "feminine",
      compoundStem: "Tochtergesellschafts",
    },
  },
  Department: {
    en: { singular: "Department", plural: "Departments" },
    de: {
      singular: "Abteilung",
      plural: "Abteilungen",
      gender: "feminine",
      compoundStem: "Abteilungs",
    },
  },
};

const ITEM_ENTITY_NAMES: Record<ItemName, LocalizedEntityName> = {
  "Tracking Item": {
    en: { singular: "Tracking Item", plural: "Tracking Items" },
    de: {
      singular: "Nachverfolgungselement",
      plural: "Nachverfolgungselemente",
      gender: "neuter",
    },
  },
  Deliverable: {
    en: { singular: "Deliverable", plural: "Deliverables" },
    de: {
      singular: "Lieferergebnis",
      plural: "Lieferergebnisse",
      gender: "neuter",
    },
  },
  Task: {
    en: { singular: "Task", plural: "Tasks" },
    de: { singular: "Aufgabe", plural: "Aufgaben", gender: "feminine" },
  },
  Activity: {
    en: { singular: "Activity", plural: "Activities" },
    de: { singular: "Aktivität", plural: "Aktivitäten", gender: "feminine" },
  },
  Milestone: {
    en: { singular: "Milestone", plural: "Milestones" },
    de: {
      singular: "Meilenstein",
      plural: "Meilensteine",
      gender: "masculine",
    },
  },
  Measure: {
    en: { singular: "Measure", plural: "Measures" },
    de: { singular: "Maßnahme", plural: "Maßnahmen", gender: "feminine" },
  },
  Finding: {
    en: { singular: "Finding", plural: "Findings" },
    de: { singular: "Befund", plural: "Befunde", gender: "masculine" },
  },
  Initiative: {
    en: { singular: "Initiative", plural: "Initiatives" },
    de: {
      singular: "Initiative",
      plural: "Initiativen",
      gender: "feminine",
      compoundStem: "Initiativ",
    },
  },
  Question: {
    en: { singular: "Question", plural: "Questions" },
    de: { singular: "Frage", plural: "Fragen", gender: "feminine" },
  },
};

/**
 * Builds a {@link TutorialEntityNames} object from the configured product
 * project/item names so that the tutorial copies use the correct localised
 * names and proper German grammar (articles, adjective endings, compound words).
 */
export function getTutorialEntityNames(
  projectName?: ProjectName,
  itemName?: ItemName
): TutorialEntityNames {
  return {
    project: projectName ? PROJECT_ENTITY_NAMES[projectName] : undefined,
    item: itemName ? ITEM_ENTITY_NAMES[itemName] : undefined,
  };
}
